/** @jsx jsx */

import { ChangeEvent, Fragment, useState } from 'react';
import { isEmpty } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import {
  Alert,
  Button,
  Field,
  FormLabel,
  ModalDialogRoot,
  ModalDialogHeader,
  ModalDialogBody,
  ModalDialogFooter,
  Radio,
  Tooltip,
  TooltipFocusableTrigger,
  RadioGroup,
  Spacer,
  Text,
  TextInput,
} from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

import Badge from 'app/developer-v3/components/Badge';
import Link from 'app/common/components/Link';
import { BASE_DOC_URL_VB, ROLES } from 'app/developer-v3/constants';
import { useAvroEvent, useIsIntegrationAdmin } from 'app/developer-v3/hooks';

const Styles = {
  labelWrapper: css`
    min-width: 120px;
  `,
  radioLabelWrapper: css`
    display: grid;
    grid-template-columns: auto auto 1fr;
    gap: 10px;
    text-align: right;
  `,
};

type Props = {
  appTitle: string;
  error?: string;
  initialEmail?: string;
  initialRole?: ROLES;
  initialWelcomeMessage?: string;
  integrationId: string;
  onCloseModal: () => void;
  submitTeamMember: (formData: {
    teamMemberEmail?: string;
    teamMemberRole?: ROLES;
    teamMemberWelcome?: string;
  }) => void;
};

const TeamMemberModal = ({
  appTitle,
  error,
  integrationId,
  initialEmail = '',
  initialRole = undefined,
  initialWelcomeMessage = '',
  onCloseModal,
  submitTeamMember,
}: Props) => {
  // Form fields
  const [email, setEmail] = useState(initialEmail);
  const [role, setRole] = useState<ROLES>(initialRole);
  const [welcomeMessage, setWelcomeMessage] = useState(initialWelcomeMessage);

  // Form Errors
  const [emailError, setEmailError] = useState<string>();
  const [roleError, setRoleError] = useState<string>();
  const [welcomeMessageError, setWelcomeMessageError] = useState<string>();

  const { isAdmin: isIntegrationAdmin } = useIsIntegrationAdmin(
    { integrationId },
    { enabled: true }
  );
  const { emitPartnerUserInteractionEvent } = useAvroEvent();

  /**
   * Validates the form data
   * @returns boolean
   */
  const validateTeamMember = () => {
    const EMAIL_TEST = /(.+)@(.+){2,}/;
    const requiredMessage = 'This is a required field.';

    if (isEmpty(email)) {
      setEmailError(requiredMessage);
    }

    if (!EMAIL_TEST.test(email)) {
      setEmailError(
        `Please include an '@' in the email address. '${email}' is missing an '@'.`
      );
    }

    if (isEmpty(role)) {
      setRoleError(requiredMessage);
    }

    if (isEmpty(welcomeMessage)) {
      setWelcomeMessageError(requiredMessage);
    }

    const hasErrors =
      isEmpty(email) ||
      !EMAIL_TEST.test(email) ||
      isEmpty(role) ||
      isEmpty(welcomeMessage);

    return hasErrors;
  };

  /**
   * Cleans up error state
   */
  const resetErrors = () => {
    setEmailError('');
    setRoleError('');
    setWelcomeMessageError('');
  };

  /** When the role is updated the suggested welcome message is different */
  const onUpdateRole = selectedRole => {
    const welcomePrompt = {
      [ROLES.admin]: `I would like you to help manage ${appTitle}'s Zapier integration and get access to see how it's performing.`,
      [ROLES.collaborator]: `I would like you to help support, share, and promote ${appTitle}'s Zapier integration.`,
      [ROLES.subscriber]: `I would like you to get reports and updates about ${appTitle}'s Zapier integration.`,
    };

    setRole(selectedRole);
    setWelcomeMessage(welcomePrompt[selectedRole]);
    setWelcomeMessageError('');
  };

  const handleSubmit = () => {
    resetErrors();

    if (!validateTeamMember()) {
      submitTeamMember({
        teamMemberEmail: email,
        teamMemberRole: role,
        teamMemberWelcome: welcomeMessage,
      });

      const eventId = uuidv4();
      emitPartnerUserInteractionEvent({
        causationId: eventId,
        eventAction: 'click',
        eventId,
        interactionGoal: 'Add team member to integration',
        interactionName: 'add_team_member',
        interactionRawText: 'Send Invite',
        targetAccessLevel: role,
        targetEmailAddress: email,
      });

      setEmail('');
      setWelcomeMessage('');
    }
  };

  return (
    <ModalDialogRoot
      isOpen
      onOpenChange={open => {
        if (!open) onCloseModal();
      }}
      aria-label="Invite Team Member"
    >
      <ModalDialogHeader textAlign="center">
        Invite Team Member
      </ModalDialogHeader>
      <ModalDialogBody>
        {error && (
          <Fragment>
            <Alert status="error">{error}</Alert>
            <Spacer height={20} />
          </Fragment>
        )}
        <Field
          error={emailError}
          inputId="team_member_email"
          isRequired={true}
          label="Team Member Email"
          renderInput={inputProps => (
            <TextInput
              {...inputProps}
              aria-label="Team Member Email"
              aria-required={true}
              id="team_member_email"
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setEmail(event.target.value);
              }}
              value={email}
            />
          )}
        />
        <Spacer height={30} />
        <Spacer height={15} />
        <RadioGroup
          errorMessage={roleError || undefined}
          isInvalid={Boolean(roleError)}
          isRequired={true}
          label={
            <div css={Styles.radioLabelWrapper}>
              <span>Team Member Role</span>
              <Link
                href={`${BASE_DOC_URL_VB}/manage/add-team`}
                style={{ textDecoration: 'underline' }}
                target="_blank"
              >
                Learn more
              </Link>
            </div>
          }
          name="teamMemberRole"
          onChange={onUpdateRole}
          value={role}
        >
          {!isIntegrationAdmin ? (
            <Tooltip content="Only admins can invite other admins">
              <TooltipFocusableTrigger tag="div">
                <FormLabel
                  htmlFor="team_member_role_admin"
                  isDisabled={!isIntegrationAdmin}
                >
                  <Radio
                    aria-label="Admin"
                    id="team_member_role_admin"
                    isDisabled={!isIntegrationAdmin}
                    value={ROLES.admin}
                  />
                  <div css={Styles.labelWrapper}>
                    <Text type="Title">Admin</Text>
                  </div>
                  <Spacer height={5} />
                  <Text>
                    Admins will be able to log in and help manage this
                    integration.
                  </Text>
                </FormLabel>
              </TooltipFocusableTrigger>
            </Tooltip>
          ) : (
            <FormLabel
              htmlFor="team_member_role_admin"
              isDisabled={!isIntegrationAdmin}
            >
              <Radio
                aria-label="Admin"
                id="team_member_role_admin"
                isDisabled={!isIntegrationAdmin}
                value={ROLES.admin}
              />
              <div css={Styles.labelWrapper}>
                <Text type="Title">Admin</Text>
              </div>
              <Spacer height={5} />
              <Text>
                Admins will be able to log in and help manage this integration.
              </Text>
            </FormLabel>
          )}
          <Spacer height={15} />
          <Radio value={ROLES.collaborator}>
            <div css={Styles.labelWrapper}>
              <Text type="Title">Collaborator</Text>
            </div>
            <Spacer height={5} />
            <div>
              <Badge>New</Badge>
            </div>
            <Text>
              Collaborators can log in to view performance data, user feedback,
              and access embed tools, but can't make changes.
            </Text>
          </Radio>
        </RadioGroup>
        <Spacer height={30} />
        {role && (
          <Fragment>
            <Field
              error={welcomeMessageError}
              isRequired={true}
              label="Add a note to your invitation"
              renderInput={inputProps => (
                <TextInput
                  {...inputProps}
                  aria-label="Welcome message"
                  aria-required={true}
                  isMultiline={true}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setWelcomeMessage(event.target.value);
                  }}
                  rows={4}
                  value={welcomeMessage}
                />
              )}
            />
            <Spacer height={30} />
          </Fragment>
        )}
      </ModalDialogBody>
      <ModalDialogFooter>
        <Button onPress={handleSubmit}>Send Invite</Button>
      </ModalDialogFooter>
    </ModalDialogRoot>
  );
};

export default TeamMemberModal;
